import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy,ChangeDetectorRef,Component,ElementRef,EventEmitter,HostListener,Input,OnChanges,OnInit,Output,SimpleChanges,ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { math } from '../../math';
import { canBeValidNumber } from '../../number';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

type CellValue=string|number|boolean;

function prevRow(ele:Element):HTMLTableRowElement | null{
	if(ele instanceof HTMLTableCellElement)
		ele=ele.parentElement as HTMLElement;
	if(ele instanceof HTMLTableRowElement && ele.previousElementSibling instanceof HTMLTableRowElement)
		return ele.previousElementSibling;
	return null;
}

function nextRow(ele:Element):HTMLTableRowElement | null{
	if(ele instanceof HTMLTableCellElement)
		ele=ele.parentElement as HTMLElement;
	if(ele instanceof HTMLTableRowElement && ele.nextElementSibling instanceof HTMLTableRowElement)
		return ele.nextElementSibling;
	return null;
}

function prevCell(ele:Element){
	if(ele instanceof HTMLTableCellElement && ele.previousElementSibling instanceof HTMLTableCellElement)
		return ele.previousElementSibling;
	return null;
}

function nextCell(ele:Element){
	if(ele instanceof HTMLTableCellElement && ele.nextElementSibling instanceof HTMLTableCellElement)
		return ele.nextElementSibling;
	return null;
}

@Component({
	standalone: true,
	selector: 'spreadsheet',
	templateUrl: './spreadsheet.component.html',
	styleUrls: ['./spreadsheet.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatCheckboxModule,
		MatIconModule,
		MatMenuModule,
		MatPaginatorModule,
	],
})
export class SpreadsheetComponent implements OnInit, OnChanges{
	constructor(
		private readonly changeDetector:ChangeDetectorRef,
	){
	}

	@Input() headers:string[]=[];
	@Input() headerTitles:{[key:string]:string}={};
	@Input() headersSwappable:string[][]=[];
	@Input() rows:CellValue[][]=[];
	@Input() paginate: boolean=false;
	@Input() paginationLength: number = 10;
	private paginationMin: number = 0;
	private paginationMax: number = this.paginationLength
	public paginatedRows:CellValue[][]=this.rows.slice(this.paginationMin, this.paginationMax);
	@Input() get columnFormats(){
		return this._columnFormats;
	}
	set columnFormats(v){
		this._columnFormats=v;
		this.columnFormatsParsed=v.filter(f => f).map(f=>{
			const a=f.split('.');
			const intDigits=+a[0];
			const b=a[1].split('-');
			const minDecimal=math.max(+b[0],1);
			const maxDecimal=math.min(+b[1],8);
			return {
				intDigits,
				minDecimal,
				maxDecimal,
			};
		});
	}

	@Output() valueChange=new EventEmitter<{ri:number,ci:number,value:CellValue}>();
	@Output() headersChange=new EventEmitter<string[]>();
	@ViewChild('table') tableRef:ElementRef<HTMLTableElement>;
	
	private _columnFormats:string[]=[];
	private columnFormatsParsed:{intDigits:number,minDecimal:number,maxDecimal:number}[]=[];
	public cellTypes:string[][]=[];
	public headerSwapOptions:string[][]=[];
	private selectedCell:HTMLTableCellElement | null=null;

	ngOnInit(): void {
		if (this.rows.length > 10) {
			this.paginate = true;
		}
		this.paginatedRows = this.paginate ? this.rows.slice(this.paginationMin, this.paginationMax) : this.rows;
	}
	ngOnChanges(changes:SimpleChanges){
		if(changes['headers'] || changes['headerTitles']){
			this.headerTitles??={};
			for(const header of this.headers){
				if(this.headerTitles[header]===undefined){
					this.headerTitles[header]=header;
				}
			}
		}
		if(changes['headers'] || changes['headers']){
			this.headerSwapOptions=this.headers.map(header=>{ 
				return this.headersSwappable.find(opts=>opts.includes(header))|| []}) ;
		}
		if(changes['rows']){
			this.cellTypes=this.rows.map(row=>row.map(v=>{
				let t=typeof(v);
				if(t==='string' && canBeValidNumber(v))
					t='number';
				return t;
			}));
		}
	}

	public getPaginatorData(event: PageEvent): PageEvent {
		this.paginationMin = event.pageIndex * event.pageSize;
		this.paginationMax = this.paginationMin + event.pageSize;
		this.paginatedRows = this.rows.slice(this.paginationMin, this.paginationMax);
		return event;
	  }

	private getValue(cell:HTMLTableCellElement){
		const ri=(<HTMLTableRowElement>cell.parentElement).rowIndex-1;
		const ci=cell.cellIndex-1;
		return this.rows[ri][ci];
	}
	
	private getCellType(cell:HTMLTableCellElement){
		const ri=(<HTMLTableRowElement>cell.parentElement).rowIndex-1;
		const ci=cell.cellIndex-1;
		return this.cellTypes[ri][ci];
	}

	private setValue(cell:HTMLTableCellElement, value:CellValue){
		const ri=(<HTMLTableRowElement>cell.parentElement).rowIndex-1;
		const ci=cell.cellIndex-1;
		if(this.rows[ri][ci]!=value){
			this.rows[ri][ci]=value;
			this.valueChange.emit({ri,ci,value});
			this.changeDetector.markForCheck();
		}
	}

	public reorderHeaders(index:number, header:string){
		if(this.headers[index]!==header){
			if(header!==''){
				const prevIndex=this.headers.findIndex(h=>h===header);
				if(prevIndex>=0)
					this.headers[prevIndex]='';
			}
			this.headers[index]=header;
			this.headersChange.emit(this.headers);
		}
	}

	public selectCell(next:HTMLTableCellElement){
		if(this.selectedCell!==next){
			const prev=this.selectedCell;
			if(prev){
				const input=prev.querySelector('input');
				if(input?.selectionDirection)
					input.selectionDirection='none';
				this.tableRef.nativeElement.focus();
				prev.classList.remove('selected');
			}
			this.selectedCell=next;
			if(next){
				next.classList.add('selected');
				next.scrollIntoView({block:'nearest'});
			}
		}
	}

	public selectCellEvt(evt:MouseEvent|FocusEvent){
		const cell=evt.composedPath().find(ele=>ele instanceof HTMLTableCellElement || ele===this.tableRef.nativeElement);
		if(cell instanceof HTMLTableCellElement){
			this.selectCell(cell);
		}
	}

	public formatNumber(columnIndex:number, n:string|number|boolean){
		const format=this.columnFormatsParsed[columnIndex] ?? {intDigits: 0, minDecimal: 0, maxDecimal:8};
		if(typeof(n)!=='number'){
			const _n=+n;
			if(isNaN(_n))
				return n;
			n=_n;
		}
		let str=n.toFixed(format.maxDecimal);
		let [intStr,decStr]=str.split('.');
		
		decStr??='';
		while(decStr.length>format.minDecimal && decStr.at(-1)==='0')
			decStr=decStr.substring(0,decStr.length-1);

		if(format.intDigits)
			intStr=intStr.padStart(format.intDigits,'0');
		if(decStr)
			str=intStr+'.'+decStr;
		else
			str=intStr;
		return str;
	}

	public onValueChanged(rowIndex:number, columnIndex:number, v:CellValue){
	}

	public onInputFocus(evt:FocusEvent){
		const cell=evt.composedPath().find(ele=>ele instanceof HTMLTableCellElement || ele===this.tableRef.nativeElement);
		if(cell instanceof HTMLTableCellElement){
			if(cell!==this.selectedCell){
				evt.preventDefault();
				evt.stopPropagation();
				this.tableRef.nativeElement.focus();
			}
		}
	}
	
	public onInputBlur(evt:FocusEvent){
		const cell=evt.composedPath().find(ele=>ele instanceof HTMLTableCellElement || ele===this.tableRef.nativeElement);
		if(cell instanceof HTMLTableCellElement){
			const input=cell.querySelector('input');
			if(input){
				this.setValue(cell,input.value);
			}
		}
	}

	public async onKeyDown(evt:KeyboardEvent){
		if(this.selectedCell){
			const selectedCell=this.selectedCell;
			if(evt.code==='Enter'){
				const next=nextRow(selectedCell)?.cells[selectedCell.cellIndex];
				if(next)
					this.selectCell(next);
				evt.preventDefault();
				evt.stopPropagation();
			}else if(!(evt.target instanceof HTMLInputElement && evt.target.type==='text')){
				if(evt.code==='ArrowUp'){
					const next=prevRow(selectedCell)?.cells[selectedCell.cellIndex];
					if(next)
						this.selectCell(next);
				}else if(evt.code==='ArrowDown' || evt.code==='Enter'){
					const next=nextRow(selectedCell)?.cells[selectedCell.cellIndex];
					if(next)
						this.selectCell(next);
				}else if(evt.code==='ArrowLeft'){
					const next=prevCell(selectedCell);
					if(next)
						this.selectCell(next);
				}else if(evt.code==='ArrowRight'){
					const next=nextCell(selectedCell);
					if(next)
						this.selectCell(next);
				}else if(evt.code==='Backspace' || evt.code==='Delete'){
					this.setValue(selectedCell,'');
				}else if(evt.ctrlKey){
					if(evt.code==='KeyX'){
						window.navigator.clipboard.writeText(this.getValue(selectedCell).toString());
						this.setValue(selectedCell,'');
					}else if(evt.code==='KeyC'){
						window.navigator.clipboard.writeText(this.getValue(selectedCell).toString());
					}else if(evt.code==='KeyV'){
						const value=await window.navigator.clipboard.readText();
						const type=this.getCellType(selectedCell);
						if(type==='boolean')
							this.setValue(selectedCell,!!value);
						else
							this.setValue(selectedCell,value);
					}
				}else if(evt.code==='Space' && this.getCellType(selectedCell)==='boolean'){
					this.setValue(selectedCell,!this.getValue(selectedCell))
				}else if(evt.key.length===1 && this.getCellType(selectedCell)!=='boolean'){
					//this.setValue(selectedCell,evt.key);
					const inputEle=this.selectedCell.querySelector('input')
					if(inputEle){
						inputEle.value=evt.key;
						inputEle.focus();
					}
				}
				evt.preventDefault();
				evt.stopPropagation();
			}
		}
	}

	public dragging:{
		srcCell:HTMLTableCellElement;
		targetableCells:HTMLTableCellElement[];
	} | null=null;

	public beginDrag(evt:MouseEvent){
		if(evt.target instanceof HTMLElement){
			const srcCell=evt.target.parentElement;
			if(srcCell instanceof HTMLTableCellElement){
				const columnIndex=srcCell.cellIndex;
				const srcRow=srcCell.parentElement;
				if(srcRow instanceof HTMLTableRowElement){
					const targetableCells:HTMLTableCellElement[]=[];
					let row=nextRow(srcRow);
					while(row instanceof HTMLTableRowElement){
						targetableCells.push(row.cells[columnIndex]);
						row=nextRow(row);
					}

					this.dragging={
						srcCell,
						targetableCells,
					};
					evt.stopPropagation();
					evt.preventDefault();
				}
			}
		}
	}

	@HostListener('document:mousemove', ['$event']) onDocumentMouseMove(evt:MouseEvent){
		if(this.dragging){
			const {targetableCells}=this.dragging;
			const rects=targetableCells.map(cell=>cell.getBoundingClientRect());
			if (rects.length === 0) return;
			let highlightedCells:HTMLTableCellElement[]=[];
			let nonHighlightedCells:HTMLTableCellElement[]=targetableCells;

			if(evt.y>rects[0].top){
				let endIndex=rects.findIndex(rect=>evt.y<rect.bottom);
				if(endIndex>=0){
					highlightedCells=targetableCells.slice(0,endIndex+1);
					nonHighlightedCells=targetableCells.slice(endIndex+1);
				}else{
					highlightedCells=targetableCells;
					nonHighlightedCells=[];
				}
			}
			highlightedCells.forEach(cell=>cell.classList.add('highlight'));
			nonHighlightedCells.forEach(cell=>cell.classList.remove('highlight'));

			evt.stopPropagation();
			evt.preventDefault();
		}
	}

	@HostListener('document:mouseup', ['$event']) onDocumentMouseUp(evt:MouseEvent){
		if(this.dragging){
			const {srcCell,targetableCells}=this.dragging;
			this.dragging=null;
			const srcContents=this.getValue(srcCell);
			for(const cell of targetableCells){
				if(cell.classList.contains('highlight')){
					this.setValue(cell,srcContents);
					cell.classList.remove('highlight');
				}
			}

			evt.stopPropagation();
			evt.preventDefault();
		}
	}
		
	public trackByLength():null{
		// this function when used in a ngFor loop disables checking individual values in a loop.
		// we need this in a ngFor loop because when the loop updates it destroys and recreates its contents
		// which is not what want when we have a loop of measurment-inputs it makes them nearly unusable
		return null;
	}	
}
